'use client'

import { useCallback, useRef } from 'react'
import { deployEnvOf, isNonProduction } from '@/lib/deploy-env'
import { PUBLIC_CHAT_OPEN_ACTION } from '@/server/domain/santa-chat/public/ports'

/** Render explícito: el script no pinta nada hasta que se lo pedimos. */
export const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
/** Clave de prueba oficial de Cloudflare (invisible, siempre pasa) para dev y Preview sin clave propia. */
const TESTING_SITE_KEY = '1x00000000000000000000BB'

/**
 * La clave del sitio, o `null` si no hay reto que pintar. La de prueba solo
 * fuera de producción, con la misma regla que el servidor (`lib/deploy-env`):
 * en Vercel lo dice `NEXT_PUBLIC_VERCEL_ENV`; sin él, solo fuera de un build
 * de producción (local, tests). En producción sin clave propia no hay reto:
 * abrir va sin token, el servidor contesta 403 y la isla dice que se vuelva a
 * intentar luego.
 */
function turnstileSiteKey(): string | null {
  const configured = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
  if (configured) return configured
  const env = deployEnvOf({ vercelEnv: process.env.NEXT_PUBLIC_VERCEL_ENV, nodeEnv: process.env.NODE_ENV })
  return isNonProduction(env) ? TESTING_SITE_KEY : null
}
/** Si Cloudflare no contesta, el botón no se queda esperando: se abre sin token y el servidor rechaza. */
const SOLVE_TIMEOUT_MS = 10_000

interface TurnstileRenderOptions {
  sitekey: string
  action: string
  execution: 'execute'
  appearance: 'interaction-only'
  callback: (token: string) => void
  'error-callback': () => void
  'timeout-callback': () => void
}

/** Lo que usamos de la API de Turnstile (`window.turnstile`). */
export interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string
  execute(widgetId: string): void
  remove(widgetId: string): void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let loading: Promise<TurnstileApi> | null = null

/** Carga el script de Cloudflare una sola vez y solo cuando se pide (DD7): la página no lo lleva. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = TURNSTILE_SCRIPT_URL
    script.async = true
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile unavailable')))
    script.onerror = () => {
      loading = null
      script.remove()
      reject(new Error('turnstile script failed to load'))
    }
    document.head.appendChild(script)
  })
  return loading
}

/** Un widget invisible por intento: se ejecuta, entrega su token (o nada) y se retira. */
function solveChallenge(api: TurnstileApi, container: HTMLElement, sitekey: string): Promise<string | null> {
  return new Promise((resolve) => {
    let widgetId: string | undefined
    const settle = (token: string | null) => {
      if (widgetId !== undefined) api.remove(widgetId)
      resolve(token)
    }
    widgetId = api.render(container, {
      sitekey,
      action: PUBLIC_CHAT_OPEN_ACTION,
      execution: 'execute',
      appearance: 'interaction-only',
      callback: (token) => settle(token),
      'error-callback': () => settle(null),
      'timeout-callback': () => settle(null),
    })
    api.execute(widgetId)
  })
}

function withTimeout<T>(work: Promise<T>, fallback: T, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<T>((resolve) => {
    timer = setTimeout(() => resolve(fallback), ms)
  })
  return Promise.race([work, timeout]).finally(() => clearTimeout(timer))
}

/**
 * Reto anti-bot del chat público (ADR-050, DD7): loader propio y perezoso. El
 * script de Cloudflare solo se pide al pulsar «Empezar», así que la landing ISR
 * no lleva scripts de terceros. `solve` nunca lanza: sin token, devuelve `null`.
 */
export function useTurnstile() {
  const containerRef = useRef<HTMLDivElement>(null)

  const solve = useCallback(async (): Promise<string | null> => {
    const container = containerRef.current
    const sitekey = turnstileSiteKey()
    if (!container || !sitekey) return null
    try {
      const solving = loadTurnstile().then((api) => solveChallenge(api, container, sitekey))
      return await withTimeout(solving, null, SOLVE_TIMEOUT_MS)
    } catch {
      return null
    }
  }, [])

  return { containerRef, solve }
}
